import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import he from "he";
import { assetUrl, bpVars } from "../../functions/themeAssets";
import { STAGE_PATHS } from "../bigpicture/gameApi";
import { bannerSrc, Icon, Loader } from "../bigpicture/components";
import { formatClock, useStage, useStopwatch } from "../bigpicture/useStage";
import Puzzle from "./Puzzle";
import "../styles/bp-puzzle.css";

const PICK_KEY = "bpPuzzleImage";

/**
 * Each player solves one slice of the big picture. It is picked at random once and kept for the
 * session so a refresh does not hand out a different piece.
 */
function pickImage(content) {
  const slices = (content.images || []).map(assetUrl);
  if (slices.length === 0) return assetUrl(content.image);
  try {
    const kept = sessionStorage.getItem(PICK_KEY);
    if (kept && slices.includes(kept)) return kept;
  } catch (_) {
    /* ignore */
  }
  const chosen = slices[Math.floor(Math.random() * slices.length)];
  try {
    sessionStorage.setItem(PICK_KEY, chosen);
  } catch (_) {
    /* ignore */
  }
  return chosen;
}

/** Challenge 4 — the jigsaw puzzle (puzzle-game.php). */
export default function PuzzleGame() {
  const navigate = useNavigate();
  const { game, finish } = useStage(4);
  const [done, setDone] = useState(false);
  const seconds = useStopwatch(game?.state?.elapsed, !!game && !done);

  const image = useMemo(() => (game ? pickImage(game.content) : ""), [game]);

  if (!game) return <Loader />;
  const theme = game.theme;

  const onSolved = async () => {
    setDone(true);
    const result = await finish({});
    if (!result) {
      setDone(false);
      return;
    }
    navigate(STAGE_PATHS[result.stage] || "/thankyou", { replace: true });
  };

  return (
    <div className="bp-puzzle" style={bpVars(theme)}>
      <div className="pg-stats-bar">
        <div className="tf-pill tf-timer-pill" aria-label="Elapsed time">
          <Icon name="clock" />
          <span id="timer-display">{formatClock(seconds)}</span>
        </div>
        <div className="tf-pill tf-points-pill" aria-label="Points">
          <Icon name="star" />
          <span id="points">{game.state.score}</span>
        </div>
      </div>

      <div className="maingame">
        <div className="auto text-center" style={{ textAlign: "center" }}>
          <div style={{ margin: "0 auto", maxWidth: 300, width: "66%" }}>
            <img
              src={bannerSrc(theme, 4)}
              className="chall"
              alt="Challenge 4"
              style={{ width: "100%", maxWidth: 300 }}
            />
          </div>
          <p className="mainHead">{he.decode(String(theme.landing_page_title4 || ""))}</p>
        </div>
      </div>

      <div className="pg-play-area">
        <div className="pg-preview-col">
          <aside className="pg-preview-card">
            <h2 className="pg-preview-title">What you're building</h2>
            <img src={image} alt="Puzzle preview" className="maskat" />
          </aside>
        </div>
        <div className="pg-puzzle-col">
          <Puzzle
            image={image}
            rows={parseInt(game.content.rows, 10) || 5}
            cols={parseInt(game.content.cols, 10) || 5}
            onSolved={onSolved}
          />
        </div>
      </div>
    </div>
  );
}
